import React, { useState } from 'react';
import { Box, Button, Typography, Snackbar, Alert, IconButton, InputAdornment } from '@mui/material';
import { FaArrowRightLong, FaArrowLeftLong } from 'react-icons/fa6';
import InputField from '../../InputField/InputField';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { forgetPassword, clearState, validateToken, changePasswordAfterForget } from '../../../redux/slices/forgetPasswordSlice';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
const ForgetPassword = ({ step, setStep, onBack, direction, t }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, error, successMessage, tokencode, tokenValidated } = useSelector((state) => state.forgetPassword);
    const [emailOrMobile, setEmailOrMobile] = useState(''); // Persist email/phone across steps
    const [successSnackbar, setSuccessSnackbar] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    // Handle Snackbar Close
    const handleCloseSnackbar = () => {
        setSuccessSnackbar(false);
        setErrorSnackbar(false);
    };
    // Detect the type of input (email or phone)
    const detectInputType = (input) => {
        const phoneRegex = /^\d+$/;  // Check if it's a valid phone number
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;  // Check if it's a valid email
        if (phoneRegex.test(input)) return 'phone';
        if (emailRegex.test(input)) return 'email';
        return null;
    };

    const handlePhoneSubmit = async (values) => {
        const payload = {
            mobile: values.input,  // Use values.input instead of values.phone
            email: '',  // No email if phone is entered
            userType: 1,
            forgetType: 2, // 2 = Phone-based reset
        };
        try {

            setEmailOrMobile(values.input);
            const result = await dispatch(forgetPassword(payload)).unwrap();
            if (result) {
                setSnackbarMessage('Reset code sent to your phone.');
                setSuccessSnackbar(true);
                setStep(2);
            }
        } catch {
            setSnackbarMessage('Failed to send reset code. Please try again.');
            setErrorSnackbar(true);
        }
    };

    const handleEmailSubmit = async (values) => {
        const payload = {
            mobile: '',
            email: values.input,  // Use values.input instead of values.email
            userType: 1,
            forgetType: 1, // 1 = Email-based reset
        };
        try {

            setEmailOrMobile(values.input);
            const result = await dispatch(forgetPassword(payload)).unwrap();
            if (result) {
                setSnackbarMessage('Reset code sent to your email.');
                setSuccessSnackbar(true);
                setStep(2);
            }
        } catch {
            setSnackbarMessage('Failed to send reset code. Please try again.');
            setErrorSnackbar(true);
        }
    };

    const handleTokenValidation = async (values) => {
        const payload = {
            emailOrMobile,
            tokencode: values.token,
            userType: 1,
        };

        // console.log("payload", payload);

        try {
            const result = await dispatch(validateToken(payload)).unwrap();

            // Check the response fields for success or error
            if (result.successtate === 200) { // Assuming 200 means success
                setSnackbarMessage('Token validated successfully.');
                setSuccessSnackbar(true);
                setStep(3); // Move to the next step
            } else {
                // If the API returns an error state, display the message
                setSnackbarMessage(result.errormessage || 'Invalid token. Please try again.');
                setErrorSnackbar(true);
            }
        } catch (error) {
            console.error('Error during token validation:', error);
            setSnackbarMessage('An error occurred while validating the token. Please try again.');
            setErrorSnackbar(true);
        }
    };

    const handleResetSubmit = (values) => {
        const payload = {
            emailOrMobile, // Dynamically pass email or phone
            tokencode,
            userType: 1,
            newPassword: values.password,
        };
        dispatch(changePasswordAfterForget(payload)).then((result) => {
            if (result.meta.requestStatus === 'fulfilled') {
                setSnackbarMessage('Password reset successfully! You can now log in.');
                setSuccessSnackbar(true);
                navigate('/signin');
            } else {
                setSnackbarMessage('Password reset failed. Please check your code or try again.');
                setErrorSnackbar(true);
            }
        }).catch((error) => {
            console.error('Error in API call:', error);
        });
    };

    // Validation schemas
    const inputValidationSchema = Yup.object().shape({
        input: Yup.string().required('Email or Phone is required'),
    });
    const tokenValidationSchema = Yup.object().shape({
        token: Yup.string().required('Reset token is required'),
    });
    const resetValidationSchema = Yup.object().shape({
        password: Yup.string()
            .required(t.passwordRequired)
            .min(8, t.passwordMinLength)
            .matches(/[A-Z]/, t.passwordUppercase) // Uppercase letter
            .matches(/[a-z]/, t.passwordLowercase) // Lowercase letter
            .matches(/\d/, t.passwordNumber)
            .matches(/[@$!%*?&]/, t.passwordSpecialChar) // Special character
            .trim(),
        confirmPassword: Yup.string()
            .required(t.confirmPasswordRequired)
            .oneOf([Yup.ref('password')], t.passwordsMustMatch),
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
            }}
        >
            <Snackbar
                open={successSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Snackbar
                open={errorSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            {step === 1 && (
                <>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            marginBottom: '32px',
                            textAlign: 'center',
                            fontSize: '16px',
                            fontWeight: '400',
                            lineHeight: '19.5px',
                        }}
                    >
                        {t.enterEmailOrPhone}
                    </Typography>

                    {error && (
                        <Typography
                            color="error"
                            sx={{ marginBottom: '16px', textAlign: 'center', fontSize: '14px' }}
                        >
                            {error}
                        </Typography>
                    )}

                    <Formik
                        initialValues={{ input: '' }}  // Bind input field to "input"
                        validationSchema={inputValidationSchema}
                        onSubmit={(values, { setSubmitting }) => {
                            const inputType = detectInputType(values.input);
                            if (inputType === 'phone') {
                                handlePhoneSubmit(values);
                            } else if (inputType === 'email') {
                                handleEmailSubmit(values);  // Ensure the correct value is passed here
                            } else {
                                setSnackbarMessage('Please enter a valid email or phone number.');
                                setErrorSnackbar(true);
                            }
                            setSubmitting(false);
                        }}
                    >
                        {({ handleSubmit, handleChange, values, errors, touched }) => (
                            <Box sx={{ width: { md: '517px', sm: '520px' } }}>
                                <InputField
                                    label={t.enterEmailOrPhoneAgain}
                                    name="input"  // Ensure "input" matches Formik's initialValues
                                    value={values.input}
                                    onChange={handleChange}
                                    error={touched.input && Boolean(errors.input)}
                                    helperText={touched.input && errors.input}
                                    direction={direction}
                                    placeholder={t.emailOrPhone}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column', // Align items vertically
                                        alignItems: 'center',   // Center items horizontally
                                        marginTop: '24px',
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        sx={{
                                            height: '40px',
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            backgroundColor: '#0E518A',
                                            '&:hover': { backgroundColor: '#072138' },
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            padding: '12px 40px',
                                        }}
                                    >
                                        {loading ? t.processing : t.sendResetCode}
                                    </Button>

                                    {/* Back to Login Link */}
                                    <Typography
                                        onClick={() => navigate('/signin')}
                                        sx={{
                                            marginTop: '20px',
                                            color: '#646464',
                                            cursor: 'pointer',
                                            fontFamily: 'Roboto',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '16.41px',
                                            textAlign: 'center',
                                            textUnderlinePosition: 'from-font',
                                            textDecorationSkipInk: 'none',
                                            display: 'flex',
                                            alignItems: 'center', // Align text and icon horizontally
                                            justifyContent: 'center', // Center the content
                                        }}
                                    >
                                        {/* Conditionally render icon based on direction */}
                                        {direction === 'rtl' ? (
                                            <Box sx={{ ml: 1, justifyContent: "center", alignItems: "center", display: "flex" }}>
                                                <FaArrowRightLong style={{ fontSize: '16px', color: '#646464' }} />
                                            </Box>
                                        ) : (
                                            <Box sx={{ mr: 1, justifyContent: "center", alignItems: "center", display: "flex" }}>
                                                <FaArrowLeftLong style={{ fontSize: '16px', color: '#646464' }} />
                                            </Box>
                                        )}
                                        {t.backToLogin}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Formik>

                </>
            )}
            {step === 2 && (
                <>
                    <Typography>{t.enterResetCode2}</Typography>
                    <Formik
                        initialValues={{ token: '' }}
                        validationSchema={tokenValidationSchema}
                        onSubmit={handleTokenValidation}
                    >
                        {({ handleSubmit, handleChange, values, errors }) => (
                            <Box sx={{ width: { md: '517px', sm: '520px' }, direction: direction }}>
                                <InputField
                                    label={t.resetToken}
                                    name="token"
                                    value={values.token}
                                    onChange={handleChange}
                                    error={!!errors.token}
                                    helperText={errors.token}

                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column', // Align items vertically
                                        alignItems: 'center',   // Center items horizontally
                                        marginTop: '24px',
                                    }}
                                >
                                    <Button
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        variant="contained"
                                        sx={{
                                            height: '40px',
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            backgroundColor: '#0E518A',
                                            '&:hover': { backgroundColor: '#072138' },
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            padding: '12px 40px',
                                        }}
                                    >
                                        {loading ? t.processing : t.validateToken}
                                    </Button>
                                    <Typography
                                        onClick={() => setStep(1)}
                                        sx={{
                                            marginTop: '20px',
                                            color: '#646464',
                                            cursor: 'pointer',
                                            fontFamily: 'Roboto',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '16.41px',
                                            textAlign: 'center',
                                            textUnderlinePosition: 'from-font',
                                            textDecorationSkipInk: 'none',
                                            display: 'flex',
                                            alignItems: 'center', // Align text and icon horizontally
                                            justifyContent: 'center', // Center the content
                                        }}
                                    >
                                        {/* Conditionally render icon based on direction */}
                                        {direction === 'rtl' ? (
                                            <Box sx={{ ml: 1, justifyContent: "center", alignItems: "center", display: "flex" }}>
                                                <FaArrowRightLong style={{ fontSize: '16px', color: '#646464' }} />
                                            </Box>
                                        ) : (
                                            <Box sx={{ mr: 1, justifyContent: "center", alignItems: "center", display: "flex" }}>
                                                <FaArrowLeftLong style={{ fontSize: '16px', color: '#646464' }} />
                                            </Box>
                                        )}
                                        {t.backToStep1}
                                    </Typography>
                                </Box>

                            </Box>
                        )}
                    </Formik>
                </>
            )}
            {step === 3 && (
                <>
                    <Snackbar
                        open={successSnackbar}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                    <Snackbar
                        open={errorSnackbar}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>

                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            mb: "32px",
                            textAlign: 'center',
                            fontSize: '16px',
                            fontWeight: '400',
                            lineHeight: '19.5px',
                        }}
                    >
                        {t.enterResetCodeAndPassword}
                    </Typography>

                    <Formik
                        initialValues={{ password: '', confirmPassword: '' }}
                        validationSchema={resetValidationSchema}
                        onSubmit={handleResetSubmit}
                    >
                        {({ handleSubmit, handleChange, values, errors, touched }) => (
                            <Box sx={{ width: { md: '517px', sm: '520px' } }}>
                                <InputField
                                    label={t.newPassword}
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    value={values.password}
                                    onChange={handleChange}
                                    direction={direction}
                                    error={touched.password && Boolean(errors.password)}
                                    helperText={touched.password && errors.password}
                                    placeholder={t.enterNewPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleClickShowPassword}>
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <InputField
                                    label={t.confirmNewPassword}
                                    name="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"} value={values.confirmPassword}
                                    onChange={handleChange}
                                    direction={direction}
                                    error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                                    helperText={touched.confirmPassword && errors.confirmPassword}
                                    placeholder={t.confirmPassword2}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleClickShowConfirmPassword}>
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column', // Align items vertically
                                        alignItems: 'center',   // Center items horizontally
                                        marginTop: '24px',
                                    }}
                                >
                                    <Button
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        variant="contained"
                                        sx={{
                                            height: '40px',
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            backgroundColor: '#0E518A',
                                            '&:hover': { backgroundColor: '#072138' },
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            padding: '12px 40px',
                                        }}
                                    >
                                        {loading ? t.processing : t.resetPassword}
                                    </Button>
                                    {/* Back to Login Link */}
                                    <Typography
                                        onClick={() => setStep(2)}
                                        sx={{
                                            marginTop: '20px',
                                            color: '#646464',
                                            cursor: 'pointer',
                                            fontFamily: 'Roboto',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '16.41px',
                                            textAlign: 'center',
                                            textUnderlinePosition: 'from-font',
                                            textDecorationSkipInk: 'none',
                                            display: 'flex',
                                            alignItems: 'center', // Align text and icon horizontally
                                            justifyContent: 'center'
                                            , direction: direction
                                        }}
                                    >
                                        {/* Conditionally render icon based on direction */}
                                        {direction === 'rtl' ? (
                                            <Box sx={{ ml: 1, justifyContent: "center", alignItems: "center", display: "flex" }}>
                                                <FaArrowRightLong style={{ fontSize: '16px', color: '#646464' }} />
                                            </Box>
                                        ) : (
                                            <Box sx={{ mr: 1, justifyContent: "center", alignItems: "center", display: "flex" }}>
                                                <FaArrowLeftLong style={{ fontSize: '16px', color: '#646464' }} />
                                            </Box>
                                        )}
                                        {t.backToStep2}
                                    </Typography>

                                </Box>
                            </Box>
                        )}
                    </Formik>
                </>
            )}
        </Box>
    );
};

export default ForgetPassword;
